import { useState } from "react";
import { t } from "i18next";
import { Popover } from "antd";
import { IconSwitch } from "@icons/index";
import { DarkTooltip } from "@components/DarkTooltip";
import { SmoothImage } from "@components/SmoothImage";
import { ThemeAwareClassName } from "@components/ThemeAwareClassName";
import { EllipsisTooltip } from "./EllipsisTooltip";
import { ReplacePopover } from "./ReplacePopover";
import { formatSalesCount, type ProductPickerValue } from "./types";
import type { UseAccountProductsResult } from "./useAccountProducts";
import { twMerge } from "tailwind-merge";

interface SelectedCardProps {
  value: ProductPickerValue;
  list: UseAccountProductsResult;
  disabled?: boolean;
  onChange: (next: ProductPickerValue) => void;
}

export const SelectedCard = ({ value, list, disabled, onChange }: SelectedCardProps) => {
  const [open, setOpen] = useState(false);
  const sold = formatSalesCount(value.salesCount);

  const handleOpenChange = (next: boolean) => {
    if (disabled && next) return;
    setOpen(next);
    if (next) void list.loadInitial();
  };

  return (
    <div className="flex w-full items-center gap-[12px] rounded-[8px] border border-kds-border-subtle bg-kds-bg-card p-[12px]">
      <div className="size-[50px] shrink-0 overflow-hidden rounded-[4px] border border-kds-border-soft bg-kds-bg-overlay">
        {value.productImage ? (
          <SmoothImage
            src={value.productImage}
            alt={value.productTitle || "product"}
            fit="cover"
            className="size-full"
          />
        ) : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-[6px]">
        <EllipsisTooltip
          title={value.productTitle || ""}
          innerClassName="text-[14px] font-semibold leading-[1.4] text-kds-fg"
        >
          {value.productTitle || "-"}
        </EllipsisTooltip>
        {(sold || value.price) && (
          <div className="flex items-center gap-[12px] text-[14px] leading-normal text-kds-fg">
            {sold ? <span className="whitespace-nowrap">{sold}</span> : null}
            {sold && value.price ? <span className="h-[16px] w-px bg-kds-border" aria-hidden /> : null}
            {value.price ? <span className="whitespace-nowrap">{value.price}</span> : null}
          </div>
        )}
      </div>
      <DarkTooltip title={t("Replace Product")} open={open ? false : undefined}>
        <Popover
          open={open}
          onOpenChange={handleOpenChange}
          trigger="click"
          placement="bottomRight"
          arrow={true}
          destroyOnHidden
          styles={{
            container: { padding: 0, background: "transparent", boxShadow: "none", borderRadius: 9 },
          }}
          content={
            <ReplacePopover
              list={list}
              currentSelectedId={value.productId}
              onCancel={() => setOpen(false)}
              onSave={(next) => {
                onChange(next);
                setOpen(false);
              }}
            />
          }
        >
          <ThemeAwareClassName
            as="div"
            className={twMerge(
              "flex items-center justify-center cursor-pointer rounded-[8px] transition-colors duration-150",
              disabled ? "pointer-events-none opacity-50" : "",
            )}
            lightClassName="bg-kds-bg-element border border-kds-border-subtle shadow-[0px_1px_3px_0px_var(--kds-shadow)]"
            darkClassName="bg-kds-bg-element hover:bg-kds-accent-soft hover:shadow-[0px_1px_3px_0px_var(--kds-shadow)]"
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={t("Replace")}
            aria-disabled={disabled || undefined}
          >
            <IconSwitch className="size-[36px] shrink-0" />
          </ThemeAwareClassName>
        </Popover>
      </DarkTooltip>
    </div>
  );
};
